import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";

export const Programs = () => {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data.programs || [])).catch(() => {});
  }, []);

  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="relative py-24 md:py-32 bg-[#F8F9FA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <div className="text-xs tracking-[0.3em] uppercase text-[#1D4E89] font-medium mb-5">
            Programs & Pricing
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0B2B40] tracking-tight leading-tight font-light">
            Invest in your{" "}
            <span className="italic">growth, wellness, and future.</span>
          </h2>
          <p className="mt-6 text-lg text-[#4A5568] font-light leading-relaxed">
            Nine focused disciplines — for individuals, families, schools and
            organizations. Every person and organization has unique goals;
            custom programs are always available.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {programs.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              className="group relative bg-white border border-slate-100 p-8 md:p-9 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(11,43,64,0.08)] flex flex-col"
              data-testid={`program-card-${p.id}`}
            >
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#1D4E89] font-medium">
                {p.category}
              </div>
              <h3 className="font-serif text-2xl text-[#0B2B40] mt-4 leading-snug">
                {p.name}
              </h3>
              <p className="font-serif italic text-[#1D4E89] mt-2 text-base">
                {p.tagline}
              </p>
              <p className="text-[#4A5568] mt-4 leading-relaxed text-[14px]">
                {p.description}
              </p>

              {p.pricing && p.pricing.length > 0 && (
                <div className="mt-7 border-t border-slate-100 pt-6">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#4A5568] font-medium mb-4">
                    Pricing
                  </div>
                  <ul className="space-y-2.5">
                    {p.pricing.map((row) => (
                      <li
                        key={row.label}
                        className="flex justify-between items-baseline gap-4 text-[14px]"
                      >
                        <span className="text-[#1A202C]">{row.label}</span>
                        <span className="font-serif text-[#0B2B40] font-medium whitespace-nowrap">
                          {row.price}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {p.includes && p.includes.length > 0 && (
                <div className="mt-6 pt-5 border-t border-slate-100">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#4A5568] font-medium mb-3">
                    Includes
                  </div>
                  <ul className="space-y-2">
                    {p.includes.map((inc) => (
                      <li
                        key={inc}
                        className="flex items-start gap-2.5 text-[13px] text-[#4A5568]"
                      >
                        <Check
                          size={13}
                          className="text-[#1D4E89] mt-1 flex-shrink-0"
                        />
                        <span>{inc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-7 pt-5 border-t border-slate-100 flex items-center justify-between gap-3">
                <Link
                  to={`/enroll/${p.id}`}
                  data-testid={`program-enroll-${p.id}`}
                  className="inline-flex items-center gap-2 text-[#0B2B40] text-sm font-medium border-b border-[#0B2B40] pb-0.5 hover:text-[#1D4E89] hover:border-[#1D4E89] transition-colors"
                >
                  Enroll <ArrowUpRight size={13} />
                </Link>
                <a
                  href="#booking"
                  data-testid={`program-book-${p.id}`}
                  className="text-[#4A5568] text-sm hover:text-[#0B2B40] transition-colors"
                >
                  Book a session
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-14 bg-white border border-slate-100 p-8 md:p-10 text-center"
        >
          <p className="font-serif italic text-xl md:text-2xl text-[#0B2B40]">
            “Every person and organization has unique goals. Customized
            coaching, wellness, and leadership programs are available upon
            request.”
          </p>
          <a
            href="#contact"
            data-testid="custom-program-cta"
            className="inline-block mt-7 bg-[#0B2B40] text-white hover:bg-[#1D4E89] px-9 py-4 text-sm font-medium tracking-wide transition-colors duration-300"
          >
            Request a Custom Program
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Programs;
